/**
 * Firebase app initialisation.
 *
 * On the web, fetches the Firebase config from `/__/firebase/init.json`
 * (served by Firebase Hosting or the emulator).  In Tauri, that endpoint
 * does not exist, so the config is bundled directly.
 *
 * All Firebase services exported here are singletons consumed by the wrapper
 * modules under `$lib/firebase/`.
 *
 * Initialisation is deduplicated via a promise guard so it is safe to call
 * `initializeFirebase()` from multiple places.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { getAnalytics, setAnalyticsCollectionEnabled, isSupported as isAnalyticsSupported, type Analytics } from 'firebase/analytics';
import { getRemoteConfig, isSupported as isRemoteConfigSupported, type RemoteConfig } from 'firebase/remote-config';
import { isTauri } from '$lib/platform/detector';

export let firebaseApp: FirebaseApp | undefined;
export let firestore: Firestore | undefined;
export let auth: Auth | undefined;
export let storage: FirebaseStorage | undefined;

/** Analytics is `null` when it fails to initialise (common in some environments). */
export let analytics: Analytics | null = null;
export let remoteConfig: RemoteConfig | undefined;

let initPromise: Promise<void> | null = null;

/** Production Firebase config bundled for Tauri builds where Hosting is not available. */
const TAURI_FIREBASE_CONFIG = import.meta.env.VITE_TAURI
  ? {
      projectId: 'digital-pvp',
      appId: '1:895329993010:web:3faf6075bee6bcefb1972b',
      storageBucket: 'digital-pvp.appspot.com',
      locationId: 'europe-west',
      apiKey: 'AIzaSyCUgAXWhGA6xlY8_0_iEzcm4w8O5lHecb0',
      authDomain: 'digital-pvp.firebaseapp.com',
      messagingSenderId: '895329993010',
      measurementId: 'G-5HXZ4PK00C',
      projectNumber: '895329993010',
    }
  : null;

/**
 * Emulator mode (`VITE_USE_EMULATORS=true` at build time), used by the E2E
 * suite. Points every service at the local Firebase Emulator Suite under the
 * offline `demo-digital-pvp` project. Tree-shaken out of normal builds.
 */
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';
const EMULATOR_PROJECT_ID = 'demo-digital-pvp';
const EMULATOR_FIREBASE_CONFIG = {
  projectId: EMULATOR_PROJECT_ID,
  appId: '1:000000000000:web:0000000000000000000000',
  apiKey: 'demo-api-key',
  authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
  storageBucket: `${EMULATOR_PROJECT_ID}.appspot.com`,
};

/**
 * Resolve the Firebase config: uses the bundled config when building for Tauri
 * (`VITE_TAURI=true` at build time), fetches from `/__/firebase/init.json` on
 * the web — the Tauri block is tree-shaken out of web bundles entirely.
 */
async function resolveConfig(): Promise<Record<string, unknown>> {
  if (import.meta.env.VITE_TAURI && TAURI_FIREBASE_CONFIG) {
    return TAURI_FIREBASE_CONFIG;
  }
  if (USE_EMULATORS) {
    return EMULATOR_FIREBASE_CONFIG;
  }
  const response = await fetch('/__/firebase/init.json');
  if (!response.ok) {
    throw new Error(
      `Failed to fetch Firebase config: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

/**
 * Initialise Firebase.
 *
 * Uses the bundled config in Tauri, fetches from `/__/firebase/init.json` on
 * the web.  Wires up Firestore (with multi-tab IndexedDB persistence), Auth,
 * Storage, Analytics (disabled by default), and Remote Config (6-hour fetch
 * interval).
 *
 * Safe to call multiple times — subsequent calls return the same promise.
 */
export function initializeFirebase(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = new Promise<void>(async (resolve, reject) => {
    try {
      const config = await resolveConfig();

      firebaseApp = initializeApp(config);
      // Cache strategy: memory-only on web, persistent disk cache on Tauri (for offline)
      firestore = initializeFirestore(firebaseApp, {
        localCache: isTauri()
          ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
          : memoryLocalCache(),
      });
      auth = getAuth(firebaseApp);
      storage = getStorage(firebaseApp);

      if (USE_EMULATORS) {
        const host = window.location.hostname;
        connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
        connectFirestoreEmulator(firestore, host, 8080);
        connectStorageEmulator(storage, host, 9199);
        resolve();
        return;
      }

      // Analytics (best-effort)
      try {
        if (await isAnalyticsSupported()) {
          analytics = getAnalytics(firebaseApp);
          setAnalyticsCollectionEnabled(analytics, false);
        }
      } catch (err) {
        console.warn('Analytics initialization skipped:', err);
        analytics = null;
      }

      // Remote Config (best-effort)
      try {
        if (await isRemoteConfigSupported()) {
          remoteConfig = getRemoteConfig(firebaseApp);
          remoteConfig.settings.minimumFetchIntervalMillis = 6 * 60 * 60 * 1000; // 6 hours
        }
      } catch (err) {
        console.warn('Remote Config initialization skipped:', err);
      }

      resolve();
    } catch (err) {
      initPromise = null; // allow retry
      reject(err);
    }
  });

  return initPromise;
}

/** Whether Firebase has been initialised and all core services are available. */
export function isFirebaseReady(): boolean {
  return !!(firebaseApp && firestore && auth && storage);
}
